import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { requestsAPI, statsAPI, providersAPI } from '../../lib/api';
import { getServiceName, formatDate, formatDateTime, formatPrice, REQUEST_STATUS } from '../../lib/constants';
import { 
  CalendarBlank, 
  CurrencyEur, 
  Star, 
  Clock, 
  CheckCircle, 
  Dog,
  TrendUp,
  Users,
  ArrowRight,
  ChartBar
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function ProviderDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [zoneRequests, setZoneRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasProfile, setHasProfile] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      try {
        await providersAPI.getProviderByUser(user?.user_id);
        setHasProfile(true);
        
        const [statsData, requestsData, zoneData] = await Promise.all([
          statsAPI.getProviderStats(),
          requestsAPI.getRequests('pending'),
          requestsAPI.getRequestsInZone()
        ]);
        
        setStats(statsData);
        setPendingRequests(requestsData);
        setZoneRequests(zoneData);
      } catch (error) {
        // No profile yet
        setHasProfile(false);
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAcceptRequest = async (requestId) => {
    try {
      await requestsAPI.updateStatus(requestId, 'accepted');
      toast.success('Pedido aceite!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao aceitar pedido');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await requestsAPI.updateStatus(requestId, 'rejected');
      toast.success('Pedido recusado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao recusar pedido');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-6">
          <div className="grid gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="h-32 animate-pulse bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!hasProfile) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Header />
        <main className="container mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-md mx-auto"
          >
            <Dog weight="duotone" className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-2xl font-bold font-outfit mb-2">
              Bem-vindo, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-muted-foreground mb-6">
              Para começar a receber pedidos, configure o seu perfil de prestador.
            </p>
            <Link to="/provider/profile">
              <Button className="rounded-full" data-testid="create-profile-btn">
                Criar Perfil
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </main>
        <MobileNav />
      </div>
    );
  }

  const RequestCard = ({ request, showActions = true }) => {
    const client = request.client || {};
    const pets = request.pets_details || [];
    
    return (
      <Card className="provider-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <Avatar>
              <AvatarImage src={client.picture} />
              <AvatarFallback>{client.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h4 className="font-semibold">{client.name}</h4>
              <Badge variant="secondary" className="text-xs">
                {getServiceName(request.service_type)}
              </Badge>
            </div>
          </div>
          
          <div className="space-y-1 text-sm text-muted-foreground mb-3">
            <p className="flex items-center gap-2">
              <CalendarBlank className="w-4 h-4" />
              {formatDateTime(request.start_date)}
            </p>
            <p className="flex items-center gap-2">
              <Dog className="w-4 h-4" />
              {pets.map(p => p.name).join(', ') || 'Animais não especificados'}
            </p>
          </div>

          {request.observations && (
            <p className="text-sm text-muted-foreground mb-3 bg-muted/50 p-2 rounded">
              "{request.observations}"
            </p>
          )}

          {showActions && (
            <div className="flex gap-2">
              <Button 
                onClick={() => handleAcceptRequest(request.request_id)}
                className="flex-1 rounded-full"
                data-testid={`accept-${request.request_id}`}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Aceitar
              </Button>
              <Button 
                variant="outline"
                onClick={() => handleRejectRequest(request.request_id)}
                className="flex-1 rounded-full"
                data-testid={`reject-${request.request_id}`}
              >
                Recusar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold font-outfit">Dashboard</h1>
            <Link to="/provider/analytics">
              <Button variant="outline" size="sm" className="rounded-full" data-testid="analytics-link">
                <ChartBar className="w-4 h-4 mr-1" /> Analytics
              </Button>
            </Link>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <CurrencyEur className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Ganhos</p>
                      <p className="font-bold text-lg">{formatPrice(stats.total_earnings)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Concluídos</p>
                      <p className="font-bold text-lg">{stats.completed_services}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pendentes</p>
                      <p className="font-bold text-lg">{stats.pending_requests}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                      <Star className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Avaliação</p>
                      <p className="font-bold text-lg">{stats.rating.toFixed(1)} ({stats.review_count})</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Pending Requests */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Pedidos Pendentes</h2>
              <Link to="/provider/requests">
                <Button variant="ghost" size="sm">
                  Ver Todos
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            {pendingRequests.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Clock weight="duotone" className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">Não tem pedidos pendentes</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {pendingRequests.slice(0, 4).map((request) => (
                  <RequestCard key={request.request_id} request={request} />
                ))}
              </div>
            )}
          </div>

          {/* Zone Requests */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Pedidos na Sua Zona</h2>
            </div>
            
            {zoneRequests.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Users weight="duotone" className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">Não há pedidos disponíveis na sua zona</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {zoneRequests.slice(0, 4).map((request) => (
                  <RequestCard key={request.request_id} request={request} />
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
